/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';

import template from './creative-set-list-page.component.html';
import {CreativeApiService} from '../creative/creative-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {CreativeSetApiService, CreativeSetModel} from './creative-set-api.service';
import {CreativeSetBulkActionModalComponent} from './creative-set-bulk-action-modal.component';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-set-list-page',
  template
})
export class CreativeSetListPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  readonly itemStateName = 'app.adv.creative-sets.creative-set';
  readonly folioItemStateName = 'app.adv.folios.folio';
  readonly creativeItemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'userState'},
    {param: 'Folio', label: 'folio'},
    {param: 'startDate'},
    {param: 'endDate'},
    {param: 'recentlyRequested'}
  ];
  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'layout', sortable: false},
    {param: 'width', label: 'dimensions', sortable: false},
    {param: '', label: 'info', sortable: false},
    {param: 'updateTime'},
    {param: '', label: 'targeting', sortable: false}
  ];

  private readonly routeList = inject(SignalAdminResourceListStateFactory).create<CreativeSetModel>(
    inject(CreativeSetApiService), {listId: 'fullCreativeSetList', baseTable: 'creativeSet'}, inject(DestroyRef)
  );
  private activeList = this.routeList;
  private embeddedList = false;
  private readonly childHost: any = {};
  private readonly permissions: any;

  @Input()
  set list(value: SignalAdminResourceListState<CreativeSetModel>) {
    if (value) {
      this.activeList = value;
      this.embeddedList = true;
    }
  }

  get list(): SignalAdminResourceListState<CreativeSetModel> {
    return this.activeList;
  }

  @Input()
  set readOnly(value: boolean) {
    this.routeList.readOnly.set(value);
  }

  constructor(
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly angularModalService: AngularModalService,
    @Inject(LocalUserPermissions) localUserPermissions: any
  ) {
    this.permissions = localUserPermissions.getAllVisPermissions();
    getListChildren(this.childHost, this.listBinding, OBJECT_TYPE.CreativeSet, this.Creative, this.listLifetime);
  }

  ngOnInit(): void {
    if (!this.embeddedList) {
      this.routeList.load();
    }
  }

  toggleChildren(creativeSet: CreativeSetModel): void {
    this.childHost.getChildren(creativeSet);
  }

  childList(creativeSetId: string): any {
    return this.childHost.children[creativeSetId];
  }

  openBulkActions(): void {
    this.angularModalService.show(CreativeSetBulkActionModalComponent, {
      class: 'modal-lg',
      initialState: {list: this.list}
    });
  }

  layoutLinkDisabled(): boolean {
    return this.permissions.MANAGE_LAYOUTS !== true;
  }
}
